import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Plus, Send, Trash2, X, ChevronRight, Package, Calendar, FileText, AlertTriangle,
  Sparkles, TrendingUp, Wrench, ShieldCheck,
} from "lucide-react";
import { useApp, API } from "../AppContext";
import { PROGERE_MODULES, CURRENT_MODULE_CODE } from "../config/progere-modules";

/** Parse une liste ligne-par-ligne */
const parseLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

export default function AdminReleases() {
  const { token, user } = useApp();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [form, setForm] = useState({
    module_code: CURRENT_MODULE_CODE,
    release_type: "patch",
    title_fr: "",
    title_en: "",
    short_description_fr: "",
    highlights_fr: "",
    improvements_fr: "",
    fixes_fr: "",
    security_updates_fr: "",
    mandatory_update: false,
    show_popup: true,
  });
  const [nextVersion, setNextVersion] = useState("—");

  const isAdmin = user?.role === "admin";

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/releases/admin/list`, authHeaders);
      setEntries(data.entries || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    refresh();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/releases/admin/next-version`, {
      params: { module_code: form.module_code, release_type: form.release_type },
      ...authHeaders,
    }).then(({ data }) => setNextVersion(data.next_version || "—")).catch(() => {});
  }, [form.module_code, form.release_type, isAdmin]);

  const loadAudit = async () => {
    try {
      const { data } = await axios.get(`${API}/releases/admin/audit-log`, authHeaders);
      setAudit(data.entries || []);
      setShowAudit(true);
    } catch { /* ignore */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/releases`, {
        module_code: form.module_code,
        release_type: form.release_type,
        title_fr: form.title_fr,
        title_en: form.title_en || form.title_fr,
        short_description_fr: form.short_description_fr,
        highlights_fr: parseLines(form.highlights_fr),
        improvements_fr: parseLines(form.improvements_fr),
        fixes_fr: parseLines(form.fixes_fr),
        security_updates_fr: parseLines(form.security_updates_fr),
        mandatory_update: form.mandatory_update,
        show_popup: form.show_popup,
      }, authHeaders);
      setCreating(false);
      setForm((f) => ({ ...f, title_fr: "", title_en: "", short_description_fr: "",
        highlights_fr: "", improvements_fr: "", fixes_fr: "", security_updates_fr: "",
        mandatory_update: false }));
      refresh();
    } catch (err) {
      alert(err?.response?.data?.detail || "Erreur");
    }
  };

  const publish = async (id) => {
    if (!window.confirm("Publier cette version ?")) return;
    await axios.post(`${API}/releases/${id}/publish`, {}, authHeaders);
    refresh();
  };

  const archive = async (id) => {
    if (!window.confirm("Archiver / supprimer cette version ?")) return;
    await axios.delete(`${API}/releases/${id}`, authHeaders);
    refresh();
  };

  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center" data-testid="admin-releases-forbidden">
        <div className="inline-block p-4 rounded-2xl" style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border)" }}>
          <AlertTriangle size={32} style={{ color: "var(--pg-warning)" }} className="mx-auto" />
          <h2 className="font-heading text-xl font-bold mt-3" style={{ color: "var(--pg-text-primary)" }}>Accès réservé</h2>
          <p className="text-sm mt-1" style={{ color: "var(--pg-text-secondary)" }}>
            Cette page est réservée aux administrateurs PROGÈRE.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="page-admin-releases">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: "var(--pg-gold)" }}>
            ADMINISTRATION
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-black mt-0.5" style={{ color: "var(--pg-text-primary)" }}>
            Versions & mises à jour
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-releases-audit-btn"
            onClick={loadAudit}
            className="text-xs font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5"
            style={{ background: "transparent", color: "var(--pg-text-secondary)", border: "1px solid var(--pg-border-strong)" }}
          >
            <FileText size={12} /> Journal d&apos;audit
          </button>
          <button
            data-testid="admin-releases-create-btn"
            onClick={() => setCreating(true)}
            className="text-xs font-bold px-4 py-2 rounded-full inline-flex items-center gap-1.5"
            style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}
          >
            <Plus size={12} /> Nouvelle version
          </button>
        </div>
      </div>

      <div className="rounded-2xl overflow-hidden" style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border)" }}>
        <table className="w-full text-sm">
          <thead style={{ background: "var(--pg-bg-tertiary)" }}>
            <tr>
              <th className="text-left px-4 py-3" style={{ color: "var(--pg-text-secondary)" }}>Module</th>
              <th className="text-left px-4 py-3" style={{ color: "var(--pg-text-secondary)" }}>Version</th>
              <th className="text-left px-4 py-3" style={{ color: "var(--pg-text-secondary)" }}>Type</th>
              <th className="text-left px-4 py-3" style={{ color: "var(--pg-text-secondary)" }}>Statut</th>
              <th className="text-left px-4 py-3" style={{ color: "var(--pg-text-secondary)" }}>Titre</th>
              <th className="text-right px-4 py-3" style={{ color: "var(--pg-text-secondary)" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center py-10" style={{ color: "var(--pg-text-muted)" }}>Chargement…</td></tr>}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10" style={{ color: "var(--pg-text-muted)" }}>Aucune version enregistrée.</td></tr>
            )}
            {entries.map((r) => (
              <tr key={r.id} data-testid={`admin-release-row-${r.version}`} style={{ borderTop: "1px solid var(--pg-border)" }}>
                <td className="px-4 py-3 font-semibold uppercase" style={{ color: "var(--pg-text-primary)" }}>{r.module_code}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-black text-[10px]"
                    style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}>
                    v{r.version}
                  </span>
                </td>
                <td className="px-4 py-3" style={{ color: "var(--pg-text-secondary)" }}>{r.release_type}</td>
                <td className="px-4 py-3">
                  <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded" style={{
                    background: r.status === "published" ? "var(--pg-success)" : r.status === "draft" ? "var(--pg-warning)" : "var(--pg-bg-tertiary)",
                    color: r.status === "archived" ? "var(--pg-text-muted)" : "#fff",
                  }}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3" style={{ color: "var(--pg-text-secondary)" }}>{r.title_fr}</td>
                <td className="px-4 py-3 text-right">
                  {r.status === "draft" && (
                    <button
                      onClick={() => publish(r.id)}
                      data-testid={`admin-release-publish-${r.version}`}
                      className="text-xs font-bold px-3 py-1.5 rounded-full mr-1 inline-flex items-center gap-1"
                      style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}
                    >
                      <Send size={11} /> Publier
                    </button>
                  )}
                  <button
                    onClick={() => archive(r.id)}
                    data-testid={`admin-release-archive-${r.version}`}
                    className="text-xs font-semibold p-1.5 rounded-full"
                    style={{ border: "1px solid var(--pg-border)", color: "var(--pg-text-muted)" }}
                    title="Archiver / supprimer"
                  >
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CREATE modal */}
      {creating && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center p-4" data-testid="admin-release-modal">
          <div className="absolute inset-0" style={{ background: "var(--pg-bg-overlay)", backdropFilter: "blur(6px)" }} onClick={() => setCreating(false)} />
          <div className="relative rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 sm:p-8"
            style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border-strong)", boxShadow: "var(--pg-shadow)" }}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: "var(--pg-gold)" }}>NOUVEAU BROUILLON</div>
                <h2 className="font-heading text-xl font-bold mt-0.5" style={{ color: "var(--pg-text-primary)" }}>Créer une version</h2>
              </div>
              <button onClick={() => setCreating(false)} className="p-2 rounded-full" style={{ background: "var(--pg-bg-tertiary)" }}>
                <X size={14} style={{ color: "var(--pg-text-muted)" }} />
              </button>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold uppercase mb-1.5 block" style={{ color: "var(--pg-text-muted)" }}>Module</label>
                  <select
                    data-testid="admin-form-module"
                    value={form.module_code}
                    onChange={(e) => setForm((f) => ({ ...f, module_code: e.target.value }))}
                    className="progere-select w-full"
                  >
                    {PROGERE_MODULES.map((m) => <option key={m.code} value={m.code}>{m.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase mb-1.5 block" style={{ color: "var(--pg-text-muted)" }}>Type</label>
                  <select
                    data-testid="admin-form-type"
                    value={form.release_type}
                    onChange={(e) => setForm((f) => ({ ...f, release_type: e.target.value }))}
                    className="progere-select w-full"
                  >
                    <option value="patch">Patch (correctif)</option>
                    <option value="minor">Minor (mineur)</option>
                    <option value="major">Major (majeur)</option>
                    <option value="security">Security</option>
                    <option value="hotfix">Hotfix</option>
                  </select>
                </div>
              </div>

              <div className="rounded-xl p-3 flex items-center justify-between" style={{ background: "var(--pg-bg-tertiary)", border: "1px solid var(--pg-border)" }}>
                <span className="text-xs" style={{ color: "var(--pg-text-muted)" }}>Prochain numéro suggéré</span>
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded font-black text-sm" style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}>
                  v{nextVersion}
                </span>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase mb-1.5 block" style={{ color: "var(--pg-text-muted)" }}>Titre (FR)</label>
                <input required data-testid="admin-form-title-fr" value={form.title_fr}
                  onChange={(e) => setForm((f) => ({ ...f, title_fr: e.target.value }))}
                  className="progere-input w-full" placeholder="Une expérience encore plus rapide…" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase mb-1.5 block" style={{ color: "var(--pg-text-muted)" }}>Titre (EN)</label>
                <input data-testid="admin-form-title-en" value={form.title_en}
                  onChange={(e) => setForm((f) => ({ ...f, title_en: e.target.value }))}
                  className="progere-input w-full" placeholder="A faster, richer experience…" />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase mb-1.5 block" style={{ color: "var(--pg-text-muted)" }}>
                  Nouveautés (une par ligne)
                </label>
                <textarea rows={3} value={form.highlights_fr} data-testid="admin-form-highlights"
                  onChange={(e) => setForm((f) => ({ ...f, highlights_fr: e.target.value }))}
                  className="progere-textarea w-full" placeholder="Nouveau tableau de contrôle&#10;Centre de notifications&#10;Synchronisation PROGÈRE PUNCH" />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase mb-1.5 block" style={{ color: "var(--pg-text-muted)" }}>Améliorations</label>
                <textarea rows={2} value={form.improvements_fr} data-testid="admin-form-improvements"
                  onChange={(e) => setForm((f) => ({ ...f, improvements_fr: e.target.value }))}
                  className="progere-textarea w-full" />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase mb-1.5 block" style={{ color: "var(--pg-text-muted)" }}>Corrections</label>
                <textarea rows={2} value={form.fixes_fr} data-testid="admin-form-fixes"
                  onChange={(e) => setForm((f) => ({ ...f, fixes_fr: e.target.value }))}
                  className="progere-textarea w-full" />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase mb-1.5 block" style={{ color: "var(--pg-text-muted)" }}>Sécurité</label>
                <textarea rows={2} value={form.security_updates_fr} data-testid="admin-form-security"
                  onChange={(e) => setForm((f) => ({ ...f, security_updates_fr: e.target.value }))}
                  className="progere-textarea w-full" />
              </div>

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: "var(--pg-text-secondary)" }}>
                  <input type="checkbox" checked={form.show_popup} data-testid="admin-form-show-popup"
                    onChange={(e) => setForm((f) => ({ ...f, show_popup: e.target.checked }))}
                    style={{ accentColor: "var(--pg-gold)" }} />
                  Afficher la fenêtre aux utilisateurs
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: "var(--pg-text-secondary)" }}>
                  <input type="checkbox" checked={form.mandatory_update} data-testid="admin-form-mandatory"
                    onChange={(e) => setForm((f) => ({ ...f, mandatory_update: e.target.checked }))}
                    style={{ accentColor: "var(--pg-danger)" }} />
                  Mise à jour obligatoire (bloquante)
                </label>
              </div>

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setCreating(false)}
                  className="text-xs font-semibold px-4 py-2.5 rounded-full flex-1"
                  style={{ background: "transparent", color: "var(--pg-text-secondary)", border: "1px solid var(--pg-border-strong)" }}>
                  Annuler
                </button>
                <button type="submit" data-testid="admin-form-submit"
                  className="text-xs font-bold px-4 py-2.5 rounded-full flex-1 inline-flex items-center justify-center gap-1.5"
                  style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}>
                  Créer le brouillon <ChevronRight size={12} />
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* AUDIT drawer */}
      {showAudit && (
        <div className="fixed inset-0 z-[75] flex items-center justify-end p-4" data-testid="admin-audit-drawer">
          <div className="absolute inset-0" style={{ background: "var(--pg-bg-overlay)", backdropFilter: "blur(4px)" }} onClick={() => setShowAudit(false)} />
          <div className="relative rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6"
            style={{ background: "var(--pg-bg-card)", border: "1px solid var(--pg-border-strong)" }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold" style={{ color: "var(--pg-text-primary)" }}>Journal d&apos;audit</h2>
              <button onClick={() => setShowAudit(false)} className="p-2 rounded-full" style={{ background: "var(--pg-bg-tertiary)" }}>
                <X size={14} style={{ color: "var(--pg-text-muted)" }} />
              </button>
            </div>
            <div className="space-y-2 text-xs">
              {audit.length === 0 ? (
                <div className="text-center py-8" style={{ color: "var(--pg-text-muted)" }}>Aucune entrée.</div>
              ) : audit.map((a) => (
                <div key={a.id} className="rounded-lg p-3" style={{ background: "var(--pg-bg-tertiary)", border: "1px solid var(--pg-border)" }}>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold" style={{ color: "var(--pg-gold)" }}>{a.action}</span>
                    <span style={{ color: "var(--pg-text-muted)" }}>·</span>
                    <span style={{ color: "var(--pg-text-secondary)" }}>{a.module_code} v{a.version}</span>
                  </div>
                  <div className="mt-1" style={{ color: "var(--pg-text-muted)" }}>
                    {a.user_email || "system"} · {new Date(a.created_at).toLocaleString("fr-CA")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
